/**
 * product-inquiry-form.js
 *
 * Gestiona el formulario "Nombre + Email" de la plantilla de producto
 * "Consulta por email". Al enviarse:
 *   1) identifica/actualiza el perfil de Klaviyo con el email real
 *      de la persona que pregunta (para que el histórico de marketing
 *      quede limpio y asociado a ella, no a la tienda).
 *   2) dispara el evento "Consulta de producto" en Klaviyo con los
 *      datos del formulario y del producto.
 *
 * La notificación a atelier@redondobrand.com NO se hace aquí: se
 * configura en Klaviyo mediante un Flow disparado por el evento
 * "Consulta de producto" con una acción "Internal Alert". Ver la
 * guía de configuración adjunta.
 *
 * Requiere que el snippet de Klaviyo (onsite tracking, normalmente
 * instalado automáticamente por la integración de Klaviyo con
 * Shopify) esté cargado en el tema, exponiendo el objeto global
 * `window.klaviyo`.
 */
(function () {
  'use strict';

  var KLAVIYO_EVENT_NAME = 'Consulta de producto';

  function setStatus(form, message, type) {
    var statusEl = form.querySelector('.product-inquiry-form__status');
    if (!statusEl) return;
    statusEl.textContent = message || '';
    statusEl.classList.remove('product-inquiry-form__status--error', 'product-inquiry-form__status--success');
    if (type) {
      statusEl.classList.add('product-inquiry-form__status--' + type);
    }
  }

  function setSubmitting(form, isSubmitting) {
    var button = form.querySelector('.product-inquiry-form__submit');
    if (!button) return;
    button.disabled = isSubmitting;
  }

  // Identifica y trackea en Klaviyo. Compatible tanto con versiones del
  // SDK que devuelven una Promise en identify() como con versiones más
  // antiguas que no la devuelven (se usa un pequeño margen de espera
  // en ese caso para asegurar que el perfil ya está identificado).
  function identifyAndTrack(identifyProperties, eventProperties) {
    if (!window.klaviyo || typeof window.klaviyo.track !== 'function') {
      return Promise.reject(new Error('klaviyo-not-available'));
    }

    function doTrack() {
      return window.klaviyo.track(KLAVIYO_EVENT_NAME, eventProperties);
    }

    var identifyResult;
    try {
      identifyResult = window.klaviyo.identify(identifyProperties);
    } catch (err) {
      return Promise.reject(err);
    }

    if (identifyResult && typeof identifyResult.then === 'function') {
      return identifyResult.then(doTrack);
    }

    return new Promise(function (resolve) {
      setTimeout(function () {
        resolve(doTrack());
      }, 300);
    });
  }

  function handleSubmit(event) {
    event.preventDefault();

    var form = event.currentTarget;
    var nombreInput = form.querySelector('input[name="nombre"]');
    var emailInput = form.querySelector('input[name="email"]');

    var nombre = (nombreInput && nombreInput.value || '').trim();
    var email = (emailInput && emailInput.value || '').trim();

    if (!nombre || !email) {
      setStatus(form, 'Por favor, rellena tu nombre y tu email.', 'error');
      return;
    }

    setStatus(form, 'Enviando...', null);
    setSubmitting(form, true);

    var productTitle = form.getAttribute('data-product-title') || '';
    var productUrl = form.getAttribute('data-product-url') || '';
    var productId = form.getAttribute('data-product-id') || '';

    var identifyProperties = {
      email: email,
      first_name: nombre
    };

    var eventProperties = {
      nombre: nombre,
      email: email,
      producto: productTitle,
      url_producto: productUrl,
      product_id: productId
    };

    identifyAndTrack(identifyProperties, eventProperties)
      .then(function () {
        setStatus(form, '¡Gracias! Hemos recibido tu consulta, te contestaremos por email en breve.', 'success');
        form.reset();
        setSubmitting(form, false);

        // Cierra el modal automáticamente a los 2.5s si sigue abierto.
        var modal = form.closest('modal-dialog');
        if (modal && typeof modal.hide === 'function') {
          setTimeout(function () {
            modal.hide();
          }, 2500);
        }
      })
      .catch(function (error) {
        console.error('[product-inquiry-form] No se pudo enviar la consulta vía Klaviyo:', error);
        setStatus(
          form,
          'No se ha podido enviar el formulario. Escríbenos directamente a atelier@redondobrand.com.',
          'error'
        );
        setSubmitting(form, false);
      });
  }

  // Cierre del modal (botón X, clic fuera de la caja y tecla Esc).
  // No depende de que el modal-dialog global del tema tenga enganchados
  // sus propios listeners: si ya funcionan, esto no interfiere (solo
  // llama a hide() otra vez); si no funcionan por lo que sea, esto
  // asegura que el pop-up siempre se pueda cerrar.
  function closeInquiryModal(modal) {
    if (typeof modal.hide === 'function') {
      modal.hide();
    } else {
      modal.removeAttribute('open');
    }
  }

  function initInquiryModalClosing() {
    var modals = document.querySelectorAll('.product-inquiry-modal');
    modals.forEach(function (modal) {
      if (modal.dataset.inquiryCloseBound === 'true') return;
      modal.dataset.inquiryCloseBound = 'true';

      var closeButton = modal.querySelector('.product-inquiry-modal__close');
      if (closeButton) {
        closeButton.addEventListener('click', function (event) {
          event.preventDefault();
          closeInquiryModal(modal);
        });
      }

      // Clic fuera de la caja del formulario (en el fondo oscurecido).
      modal.addEventListener('click', function (event) {
        if (!event.target.closest('[role="dialog"]')) {
          closeInquiryModal(modal);
        }
      });

      // Tecla Escape, solo si el modal está abierto.
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && modal.hasAttribute('open')) {
          closeInquiryModal(modal);
        }
      });
    });
  }

  function init() {
    var forms = document.querySelectorAll('.product-inquiry-form');
    forms.forEach(function (form) {
      if (form.dataset.inquiryBound === 'true') return;
      form.dataset.inquiryBound = 'true';
      form.addEventListener('submit', handleSubmit);
    });

    initInquiryModalClosing();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Por si la sección se vuelve a renderizar en el editor de temas.
  document.addEventListener('shopify:section:load', init);
})();